import { useLocation } from '@gatsbyjs/reach-router';
import type { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import RoundedButton from '@components/RoundedButton';

import * as ROUTES from '@constants/routes';

import { useGetAllUsers } from '@hooks/api/user';
import { useNavigate } from '@hooks/useNavigate';

import { formatDate, formatName } from '@utils/format';

import type { GetAllUsersQuery } from 'generated/user.generated';

const messages = defineMessages({
  name: {
    id: 'admin.users.name',
    defaultMessage: 'Name',
  },
  email: {
    id: 'admin.users.email',
    defaultMessage: 'Email',
  },
  account: {
    id: 'admin.users.account',
    defaultMessage: 'Account',
  },
  lastLogin: {
    id: 'admin.users.lastLogin',
    defaultMessage: 'Last Login',
  },
  createdAt: {
    id: 'admin.users.createdAt',
    defaultMessage: 'Created At',
  },
  roles: {
    id: 'admin.users.roles',
    defaultMessage: 'Roles',
  },
  noData: {
    id: 'admin.users.noData',
    defaultMessage: 'No users',
  },
  create: {
    id: 'admin.users.create',
    defaultMessage: 'New User',
  },
});

interface RowData {
  id: string;
  name: string;
  account: string;
  roles: string[];
  last_login: string | null;
  created_at: string;
}

interface UsersListProps {
  data?: GetAllUsersQuery['getAllUsers'];
  loading?: boolean;
  /** Account ID passed to user creation */
  account_id?: string;
}

function UsersList({ data = [], loading = false, account_id }: UsersListProps) {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { navigateRoute } = useNavigate();

  const tableData = React.useMemo(
    () =>
      data.map<RowData>((user) => {
        const { email, first_name, middle_name, last_name } = user;
        return {
          id: user.user_id,
          name: formatName(first_name, middle_name, last_name) ?? '',
          email,
          account: user.account.account_name ?? '',
          roles: user.roles
            .map((role) => role.role_name)
            .sort((a, b) => a.localeCompare(b)),
          last_login: user.last_login ?? null,
          created_at: user.created_at,
        };
      }, []),
    [data]
  );

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _rowData,
    { dataIndex }
  ) => {
    const { id } = tableData[dataIndex];
    navigateRoute(`${ROUTES.ADMIN_MANAGE_USERS}${id}`);
  };

  const handleCreate = () => {
    if (account_id) {
      navigateRoute(ROUTES.ADMIN_MANAGE_USERS_CREATE, {
        account_id,
      });
    } else {
      navigateRoute(ROUTES.ADMIN_MANAGE_USERS_CREATE);
    }
  };

  return (
    <ContentLoader loading={loading}>
      <MuiDataTable
        columns={[
          {
            name: 'name',
            className: 'blue-hover',
            label: formatMessage(messages.name),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'email',
            className: 'small-light',
            label: formatMessage(messages.email),
            options: {
              searchable: true,
              sort: true,
              display: location.pathname.includes(ROUTES.ACCOUNTS),
            },
          },
          {
            name: 'account',
            className: 'small-light',
            label: formatMessage(messages.account),
            options: {
              searchable: true,
              sort: true,
              display: location.pathname.includes(
                `${ROUTES.ADMIN_MANAGEMENT}users`
              ),
            },
          },
          {
            name: 'roles',
            className: 'small',
            label: formatMessage(messages.roles),
            options: {
              searchable: true,
              customBodyRender: (value: RowData['roles']) => value.join(', '),
            },
          },
          {
            name: 'last_login',
            className: 'date',
            label: formatMessage(messages.lastLogin),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['last_login']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
            desktopOnly: true,
          },
          {
            name: 'created_at',
            className: 'date',
            label: formatMessage(messages.createdAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['created_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
        ]}
        data={tableData}
        options={{
          search: true,
          sort: true,
          filter: true,
          sortOrder: {
            name: 'name',
            direction: 'asc',
          },
          onRowClick: handleRowClick,
          textLabels: {
            body: {
              noMatch: formatMessage(messages.noData),
            },
          },
          customToolbar: () => {
            return (
              <RoundedButton
                autoSize
                iconName="plus"
                margin={0}
                onClick={handleCreate}
              >
                {formatMessage(messages.create)}
              </RoundedButton>
            );
          },
        }}
      />
    </ContentLoader>
  );
}

function AdminUsersList() {
  const { data, loading } = useGetAllUsers();

  return <UsersList data={data} loading={loading} />;
}

export default AdminUsersList;
export { UsersList };
